/* Native PDP bundle deal — replaces the paid Moonbundles app.
   Manages the hidden id / quantity / selling_plan inputs the wrapped
   <product-form> submits, plus per-card price display and the ATC label.

   Modes:
     once — 1 Pack = qty 1; 2 Pack = qty 2 (repriced by the admin "10% off
            min qty 2" automatic discount, mirrored here for display); 3 Pack
            = qty 4 (4th zeroed by the "Buy 3 Get 1 Free" automatic discount).
     sub  — each pack adds its own qty (1/2/3) with the selling plan whose
            percentage matches the tier target (15/20/25 by default). Among
            same-percentage plans, prefers the delivery interval closest to
            supplyDays(variant) x pack size. */
(function () {
  // Ported from assets/afterpay-message.js — formats cents using shop.money_format.
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var pattern = format && format.length ? format : '${{amount}}';

    function thousand(num, dec, thousands, decimal) {
      dec = dec == null ? 2 : dec;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(num) || num == null) return '0';
      var n = (Math.abs(cents) / 100.0).toFixed(dec);
      var parts = n.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var rest = parts[1] ? decimal + parts[1] : '';
      return dollars + rest;
    }

    var re = /\{\{\s*(\w+)\s*\}\}/;
    var match = pattern.match(re);
    var key = match ? match[1] : 'amount';
    var value;
    switch (key) {
      case 'amount_no_decimals': value = thousand(cents, 0); break;
      case 'amount_with_comma_separator': value = thousand(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = thousand(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = thousand(cents, 2, ' ', '.'); break;
      case 'amount_with_apostrophe_separator': value = thousand(cents, 2, "'", '.'); break;
      default: value = thousand(cents, 2); break;
    }
    return pattern.replace(re, value);
  }

  // Parse a selling plan name into delivery-interval days, e.g.
  // "Delivery every 45 days" -> 45, "Every 2 months" -> 60, "Monthly" -> 30.
  function planDays(name) {
    if (!name) return 0;
    var m = String(name).match(/(\d+)\s*(day|week|month|year)/i);
    if (m) {
      var n = parseInt(m[1], 10);
      var unit = m[2].toLowerCase();
      if (unit === 'day') return n;
      if (unit === 'week') return n * 7;
      if (unit === 'month') return n * 30;
      return n * 365;
    }
    if (/month/i.test(name)) return 30;
    if (/week/i.test(name)) return 7;
    return 0;
  }

  // "Ships every 45 days" / "Ships every 3 months" / "Ships every 4.5 months".
  // Short intervals read best in days; 60+ days read best in (half-)months.
  function freqLabel(days) {
    if (!days) return '';
    if (days >= 60) {
      var m = Math.round(days / 15) / 2;
      return m === 1 ? 'Ships every month' : 'Ships every ' + m + ' months';
    }
    return 'Ships every ' + days + ' days';
  }

  // --- Pre-order timeline date helpers ---
  function startOfDay(d) {
    var c = new Date(d);
    c.setHours(0, 0, 0, 0);
    return c;
  }

  function addDays(date, days) {
    var c = new Date(date);
    c.setDate(c.getDate() + days);
    return c;
  }

  function daysBetween(a, b) {
    return Math.round((startOfDay(b) - startOfDay(a)) / 86400000);
  }

  // "Mon 8 Jul" — uppercased to match the brand's date styling.
  function formatShipDate(date) {
    return date
      .toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })
      .replace(/,/g, '')
      .toUpperCase();
  }

  class BundleDeal extends HTMLElement {
    connectedCallback() {
      this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';

      this.variants = this.readJSON('[data-bundle-variants]') || [];
      // Selling plan metadata (id/name/percent) — absent when subs are off.
      this.planMeta = this.readJSON('[data-bundle-plan-meta]') || [];
      this.idInput = this.querySelector('[data-bundle-id]');
      this.qtyInput = this.querySelector('[data-bundle-quantity]');
      this.planInput = this.querySelector('[data-bundle-selling-plan]');
      this.atc = this.querySelector('[data-bundle-atc]');
      this.atcLabel = this.querySelector('[data-bundle-atc-label]');
      this.atcDefaultLabel = this.atcLabel ? this.atcLabel.textContent.trim() : 'Add to cart';
      // Variant ids that are sold out but still purchasable (computed in Liquid).
      this.preorderIds = (this.dataset.preorderIds || '').split(',').filter(Boolean);
      // Pre-order delivery timeline (shown only when the variant is oversold).
      this.timeline = this.querySelector('[data-bundle-timeline]');

      this.tierRows = Array.prototype.slice.call(this.querySelectorAll('[data-bundle-tier]'));
      this.tierInputs = Array.prototype.slice.call(this.querySelectorAll('[data-bundle-tier-input]'));
      this.subFreq = this.querySelector('[data-bundle-sub-freq]');
      this.modePrices = Array.prototype.slice.call(this.querySelectorAll('[data-bundle-mode-price]'));
      this.modeInputs = Array.prototype.slice.call(this.querySelectorAll('[data-bundle-mode-input]'));
      this.optionInputs = Array.prototype.slice.call(this.querySelectorAll('[data-bundle-option]'));
      // Conditional reward badges above the ATC.
      this.perks = this.querySelector('[data-bundle-perks]');
      this.perkEls = {};
      if (this.perks) {
        this.perks.querySelectorAll('[data-perk]').forEach((p) => { this.perkEls[p.dataset.perk] = p; });
        this.giftWorth = this.perks.querySelector('[data-perk-gift-worth]');
        this.introPerkLabel = this.perks.querySelector('[data-perk-intro-label]');
        this.introPerkDefault = this.introPerkLabel ? this.introPerkLabel.textContent : '';
        this.shipLabel = this.perks.querySelector('[data-perk-ship-label]');
        this.supplyLabel = this.perks.querySelector('[data-perk-supply-label]');
        this.subLabel = this.perks.querySelector('[data-perk-sub-label]');
      }
      // Per-variant supply days (one unit); multiplied by tier units below.
      this.supplyDays = this.readJSON('[data-bundle-supply-days]') || {};

      // Tier targets: subscription % per pack + the one-time 2-pack %.
      this.subPcts = {
        1: parseInt(this.dataset.subPct1, 10) || 15,
        2: parseInt(this.dataset.subPct2, 10) || 20,
        3: parseInt(this.dataset.subPct3, 10) || 25
      };
      this.oncePct2 = parseInt(this.dataset.oncePct2, 10) || 0;
      // One-time packs at/over this (cents) show a "Ships free" badge.
      this.shipThreshold = parseInt(this.dataset.shipThreshold, 10) || 0;
      // Store-credit earn % (display-only mirror of the Redeemly rule; 0 = off).
      this.creditPct = parseInt(this.dataset.creditPct, 10) || 0;
      // Shared under-grid line: ships-all + credit segments, | between.
      this.underGrid = this.querySelector('[data-bundle-under-grid]');
      this.shipsAllSeg = this.querySelector('[data-bundle-ships-all]');
      this.underSep = this.querySelector('[data-bundle-under-sep]');
      this.creditSeg = this.querySelector('[data-bundle-credit]');
      this.creditAmt = this.querySelector('[data-bundle-credit-amt]');
      this.creditBadgeAmt = this.querySelector('[data-bundle-credit-badge]');
      // First-order code value in cents (0 = no claimable code configured).
      this.introValue = parseInt(this.dataset.introValue, 10) || 0;
      // "jar" / "pack" / "bottle" — for the per-unit cost line on the cards.
      this.unitNoun = this.dataset.unitNoun || 'unit';
      this.introClaimed = false;

      this.state = {
        mode: this.modeInputs.length ? (this.dataset.defaultMode || 'sub') : 'once',
        tier: this.dataset.defaultTier || '1'
      };

      this.tierInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) { this.state.tier = input.value; this.render(); }
        });
      });
      this.modeInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked) { this.state.mode = input.value; this.render(); }
        });
      });
      this.optionInputs.forEach((input) => {
        input.addEventListener('change', () => { this.render(); });
      });

      // First-order claim: chip opens a confirm modal; confirming hits
      // Shopify's /discount/CODE route, which drops the code into the checkout
      // session — no email gate, applies at checkout automatically.
      var claim = this.querySelector('[data-intro-claim]');
      if (claim) {
        var self = this;
        var modal = this.querySelector('[data-claim-modal]');
        var confirmBtn = modal && modal.querySelector('[data-claim-confirm]');
        var confirmLabel = modal && modal.querySelector('[data-claim-confirm-label]');
        var blurb = modal && modal.querySelector('[data-claim-blurb]');

        var applied = function () {
          claim.textContent = '\u2713 Applied at checkout';
          claim.disabled = true;
          claim.classList.add('is-claimed');
          self.introClaimed = true;
        };
        var closeModal = function () {
          if (modal) { modal.hidden = true; modal.classList.remove('is-open'); }
        };

        // Claimed state persists 7 days per device; after that we stop
        // asserting the discount (we can't verify eligibility client-side).
        try {
          var stored = JSON.parse(window.localStorage.getItem('sp-intro-claimed') || 'null');
          if (stored && stored.code === claim.dataset.discountCode
              && Date.now() - stored.at < 7 * 86400000) applied();
        } catch (e) { /* private mode / old format */ }

        claim.addEventListener('click', function () {
          if (claim.disabled || !modal) return;
          // In Subscribe mode the chip is greyed out (the code is one-time
          // only) — a tap there means "switch to One-time", not "claim".
          if (self.state.mode === 'sub') {
            var once = self.modeInputs.filter(function (i) { return i.value === 'once'; })[0];
            if (once) {
              once.checked = true;
              self.state.mode = 'once';
              self.render();
            }
            return;
          }
          modal.hidden = false;
          modal.classList.add('is-open');
          if (confirmBtn) confirmBtn.focus();
        });

        if (modal) {
          modal.addEventListener('click', function (e) {
            if (e.target.closest('[data-claim-cancel]')) closeModal();
          });
          document.addEventListener('keyup', function (e) {
            if (e.key === 'Escape' && !modal.hidden) closeModal();
          });
        }

        if (confirmBtn) {
          confirmBtn.addEventListener('click', function () {
            if (confirmBtn.disabled) return;
            confirmBtn.disabled = true;
            if (confirmLabel) confirmLabel.textContent = 'Applying\u2026';
            fetch('/discount/' + encodeURIComponent(claim.dataset.discountCode))
              .then(function () {
                applied();
                self.render();
                try {
                  window.localStorage.setItem('sp-intro-claimed',
                    JSON.stringify({ code: claim.dataset.discountCode, at: Date.now() }));
                } catch (e) { /* ignore */ }
                if (confirmLabel) confirmLabel.textContent = '\u2713 Locked in';
                if (blurb) blurb.textContent = 'Done — your discount will show on the checkout page.';
                setTimeout(closeModal, 1400);
              })
              .catch(function () {
                confirmBtn.disabled = false;
                if (confirmLabel) confirmLabel.textContent = 'Try again';
                if (blurb) blurb.textContent = "Couldn't reach the store — check your connection and try again.";
              });
          });
        }
      }

      // ⓘ on the credit line: tap toggles the tooltip bubble; any tap
      // outside the credit segment closes it. Guarded so theme-editor
      // reconnects don't stack duplicate document listeners.
      var creditInfo = this.querySelector('[data-credit-info]');
      var creditTip = this.querySelector('[data-credit-tip]');
      if (creditInfo && creditTip && !this._creditTipBound) {
        this._creditTipBound = true;
        creditInfo.addEventListener('click', function (e) {
          e.stopPropagation();
          creditTip.hidden = !creditTip.hidden;
          creditInfo.setAttribute('aria-expanded', String(!creditTip.hidden));
        });
        document.addEventListener('click', function (e) {
          if (creditTip.hidden || e.target.closest('[data-bundle-credit]')) return;
          creditTip.hidden = true;
          creditInfo.setAttribute('aria-expanded', 'false');
        });
      }

      // Reflect the defaults in the radios.
      var selTier = this.tierInputs.filter((i) => i.value === this.state.tier)[0] || this.tierInputs[0];
      if (selTier) { selTier.checked = true; this.state.tier = selTier.value; }
      var selMode = this.modeInputs.filter((i) => i.value === this.state.mode)[0];
      if (selMode) { selMode.checked = true; this.state.mode = selMode.value; }

      this.render();
    }

    readJSON(selector) {
      var el = this.querySelector(selector);
      if (!el) return null;
      try { return JSON.parse(el.textContent); } catch (e) { return null; }
    }

    currentVariant() {
      if (this.variants.length <= 1 || !this.optionInputs.length) return this.variants[0];
      var selected = {};
      this.optionInputs.forEach(function (input) {
        if (input.checked) selected[input.dataset.bundleOption] = input.value;
      });
      var match = this.variants.filter(function (variant) {
        return (variant.options || []).every(function (value, index) {
          var pos = String(index + 1);
          return selected[pos] === undefined || selected[pos] === value;
        });
      })[0];
      return match || this.variants[0];
    }

    subEnabled() {
      return this.modeInputs.length > 0 && this.planMeta.length > 0;
    }

    subActive() {
      return this.state.mode === 'sub' && this.subEnabled();
    }

    variantSupplyDays(variant) {
      return this.supplyDays[variant.id] || this.supplyDays[String(variant.id)] || 0;
    }

    // The actual % a plan takes off this variant (allocation price beats the
    // Liquid percent, so fixed-amount / price plans still display truthfully).
    planPercentFor(variant, plan) {
      var alloc = (variant.selling_plan_allocations || []).filter(function (a) {
        return String(a.selling_plan_id) === String(plan.id);
      })[0];
      if (alloc && variant.price) return Math.round((1 - alloc.price / variant.price) * 100);
      return typeof plan.percent === 'number' ? Math.round(plan.percent) : 0;
    }

    // Resolve the selling plan for a tier: nearest percentage to the tier
    // target first, then the delivery interval closest to the variant's
    // supply days x pack size. Only plans actually allocated to the variant
    // are considered.
    planForTier(variant, tier) {
      if (!this.subEnabled()) return null;
      var allocIds = (variant.selling_plan_allocations || []).map(function (a) {
        return String(a.selling_plan_id);
      });
      var plans = this.planMeta.filter(function (p) { return allocIds.indexOf(String(p.id)) !== -1; });
      if (!plans.length) return null;

      var target = this.subPcts[tier] || 0;
      var wantDays = this.variantSupplyDays(variant) * parseInt(tier, 10);
      var self = this;
      var scored = plans.map(function (p) {
        return {
          plan: p,
          pctDiff: Math.abs(self.planPercentFor(variant, p) - target),
          dayDiff: wantDays ? Math.abs((planDays(p.name) || wantDays) - wantDays) : 0
        };
      });
      scored.sort(function (a, b) { return (a.pctDiff - b.pctDiff) || (a.dayDiff - b.dayDiff); });
      return scored[0].plan;
    }

    allocPrice(variant, planId) {
      var alloc = (variant.selling_plan_allocations || []).filter(function (a) {
        return String(a.selling_plan_id) === String(planId);
      })[0];
      return alloc ? alloc.price : variant.price;
    }

    // Everything the render pass needs for one tier in the current mode.
    // `mode` ('once' | 'sub') overrides the current mode — the mode pills
    // price the selected pack in both modes at once.
    tierConfig(variant, tier, mode) {
      var units = parseInt(tier, 10);
      var base = variant.price;
      var sub = mode ? (mode === 'sub' && this.subEnabled()) : this.subActive();

      if (sub) {
        var plan = this.planForTier(variant, tier);
        var unit = plan ? this.allocPrice(variant, plan.id) : base;
        var pct = plan ? Math.round((1 - unit / base) * 100) : 0;
        return {
          addQty: units,
          supplyUnits: units,
          total: unit * units,
          compare: pct > 0 ? base * units : 0,
          pill: pct > 0 ? 'Save ' + pct + '%' : '',
          // No ship badge on sub cards — the benefits list already says it.
          ship: false,
          meta: plan ? freqLabel(planDays(plan.name)) : '',
          plan: plan,
          pct: pct,
          gift: false
        };
      }

      // One-time cards: free shipping reads as a plain text line, not a badge
      // ("+ 1 Free" lives in the 3 Pack title + jar fan, so no pill there).
      if (tier === '3') {
        // Buy 3 Get 1 Free only: qty 4, the 4th zeroed by the BOGO discount.
        // The min-qty-2 % discount must NOT combine with it in admin — BOGO
        // always beats 10%, so Shopify applies BOGO alone on qty 4.
        return {
          addQty: 4, supplyUnits: 4, total: base * 3, compare: base * 4,
          pill: '', ship: false, meta: this.onceShipText(base * 3),
          plan: null, pct: 0, gift: true
        };
      }
      if (tier === '2') {
        // Display mirrors the admin "X% off min qty 2" one-time discount.
        var pct2 = this.oncePct2;
        var total2 = Math.round(base * 2 * (100 - pct2) / 100);
        return {
          addQty: 2, supplyUnits: 2,
          total: total2,
          compare: pct2 > 0 ? base * 2 : 0,
          pill: pct2 > 0 ? 'Save ' + pct2 + '%' : '',
          ship: false, meta: this.onceShipText(total2),
          plan: null, pct: pct2, gift: false
        };
      }
      return {
        addQty: 1, supplyUnits: 1, total: base, compare: 0, pill: '',
        ship: false, meta: this.onceShipText(base),
        plan: null, pct: 0, gift: false
      };
    }

    onceShipText(totalCents) {
      return this.shipsFree(totalCents) ? '+ Ships free (AU)' : '';
    }

    // One-time pack meets the free-shipping threshold?
    shipsFree(totalCents) {
      return this.shipThreshold > 0 && totalCents >= this.shipThreshold;
    }

    render() {
      var variant = this.currentVariant();
      if (!variant) return;
      var subbed = this.subActive();
      // Out of stock but still purchasable (oversell allowed) => pre-order.
      var preorder = this.preorderIds.indexOf(String(variant.id)) !== -1;
      this.updateTimeline(preorder && variant.available);

      var baseDays = this.variantSupplyDays(variant);

      // One-time mode: if every pack ships free, collapse the three per-card
      // "+ Ships free" lines into one line under the grid.
      var allShip = !subbed;
      if (!subbed) {
        for (var ti = 1; ti <= 3; ti++) {
          var tcfg = this.tierConfig(variant, String(ti));
          if (!tcfg.meta) { allShip = false; break; }
        }
      }
      // (under-grid line segments are toggled below, once `sel` is known —
      // the credit segment needs the selected pack's total.)

      this.tierRows.forEach((row) => {
        var cfg = this.tierConfig(variant, row.dataset.bundleTier);
        var priceEl = row.querySelector('[data-bundle-tier-price]');
        var servingEl = row.querySelector('[data-bundle-tier-serving]');
        var shipEl = row.querySelector('[data-bundle-tier-ship]');
        var metaEl = row.querySelector('[data-bundle-tier-meta]');
        var footEl = row.querySelector('[data-bundle-tier-foot]');

        // Cost per unit RECEIVED — the free 4th jar pulls this down.
        var unitMoney = formatMoney(Math.round(cfg.total / cfg.supplyUnits), this.moneyFormat);
        // Cards lead with the per-unit price only — the pack total lives in
        // the mode pills + ATC.
        if (priceEl) {
          priceEl.innerHTML = unitMoney + '<span class="bundle-deal__price-suffix">/' + this.unitNoun + '</span>';
        }
        // Footer tab under the card: "Save X%", or "Free Jar" on the one-time
        // 3 Pack (the free 4th unit).
        if (footEl) {
          var foot = cfg.gift
            ? 'Free ' + this.unitNoun.charAt(0).toUpperCase() + this.unitNoun.slice(1)
            : cfg.pill;
          footEl.textContent = foot;
          footEl.hidden = !foot;
          row.classList.toggle('no-foot', !foot);
        }
        if (servingEl) {
          var totalDays = baseDays * cfg.supplyUnits;
          if (totalDays) {
            servingEl.textContent = formatMoney(Math.round(cfg.total / totalDays), this.moneyFormat) + '/serving';
            servingEl.hidden = false;
          } else {
            servingEl.hidden = true;
          }
        }
        if (shipEl) shipEl.hidden = !cfg.ship;
        // Sub cadence lives in the Subscribe pill only; cards keep the
        // one-time "+ Ships free" line.
        if (metaEl) metaEl.textContent = (subbed || allShip) ? '' : cfg.meta;
        // Hover tooltip (desktop): the JS Health-style per-card sentence.
        if (subbed && cfg.plan && baseDays) {
          row.title = (baseDays * cfg.supplyUnits) + '-day supply, ' + cfg.meta.toLowerCase()
            + '. Skip or cancel anytime.';
        } else {
          row.removeAttribute('title');
        }
        row.classList.toggle('is-selected', row.dataset.bundleTier === this.state.tier);
      });

      // Selected-state class on the mode cards (no :has() dependency).
      this.modeInputs.forEach(function (input) {
        var card = input.closest('.bundle-mode__card');
        if (card) card.classList.toggle('is-selected', input.checked);
      });

      this.classList.toggle('is-sub-mode', subbed);
      this.classList.toggle('has-free-shipping-tier', subbed || this.state.tier === '2' || this.state.tier === '3');
      // Free gift is one-time only — suppress its framing while subscribed.
      this.classList.toggle('has-sub-no-gift', subbed);

      var sel = this.tierConfig(variant, this.state.tier);

      // Claimed first-order code: reflect the order-level $ off in the ATC /
      // sticky / afterpay totals (one-time mode only — the code excludes subs).
      var introOff = (!subbed && this.introClaimed && this.introValue > 0)
        ? Math.min(this.introValue, sel.total) : 0;
      var payTotal = sel.total - introOff;

      // Mode pills carry the selected pack's total (+ struck compare-at) in
      // each mode, so the cards don't have to.
      this.modePrices.forEach((el) => {
        var m = el.dataset.bundleModePrice;
        var c = m === (subbed ? 'sub' : 'once') ? sel : this.tierConfig(variant, this.state.tier, m);
        var off = m === 'once' ? ((this.introClaimed && this.introValue > 0) ? Math.min(this.introValue, c.total) : 0) : 0;
        var strike = off ? (c.compare || c.total) : c.compare;
        el.innerHTML = (strike ? '<s class="bundle-mode__price-was">' + formatMoney(strike, this.moneyFormat) + '</s>' : '')
          + '<span class="bundle-mode__price-now">' + formatMoney(c.total - off, this.moneyFormat) + '</span>';
      });
      // Sub pill's delivery line — cadence is driven by the selected pack.
      if (this.subFreq) {
        var subCfg = subbed ? sel : this.tierConfig(variant, this.state.tier, 'sub');
        this.subFreq.textContent = subCfg.meta.replace(/^Ships every/, 'Delivery every');
        this.subFreq.parentNode.hidden = !subCfg.meta;
      }

      // Store credit earned on the selected pack — 10% (creditPct) of the
      // total actually charged (after a claimed intro code), ceiled to a
      // whole dollar ($26.99 -> $27). Redeemly caps the earn at $30/order.
      var credit = this.creditPct ? Math.min(30, Math.ceil((payTotal * this.creditPct) / 10000)) : 0;
      if (this.creditAmt) this.creditAmt.textContent = '$' + credit;
      if (this.creditSeg) this.creditSeg.hidden = credit <= 0;
      if (this.shipsAllSeg) this.shipsAllSeg.hidden = !allShip;
      if (this.underSep) this.underSep.hidden = !(allShip && credit > 0);
      if (this.underGrid) this.underGrid.hidden = !(allShip || credit > 0);

      this.updatePerks(variant, sel, baseDays, credit);

      // Expose the current totals for the sticky bar: mode-aware price plus a
      // strikeout compare (the regular price for the same units).
      this.dataset.stickyPrice = formatMoney(payTotal, this.moneyFormat);
      this.dataset.stickyUnits = this.state.tier;
      this.dataset.stickyCompare = introOff
        ? formatMoney(sel.compare || sel.total, this.moneyFormat)
        : (sel.compare ? formatMoney(sel.compare, this.moneyFormat) : '');

      // Expose the selected bundle total so the afterpay message can divide it
      // by 4. Subscriptions can't be funded by Afterpay, so flag them and let
      // the afterpay element hide itself.
      this.dataset.afterpayCents = payTotal;
      this.dataset.afterpaySubscribed = subbed ? '1' : '';

      if (this.idInput) { this.idInput.value = variant.id; this.idInput.disabled = false; }
      if (this.qtyInput) this.qtyInput.value = sel.addQty;
      if (this.planInput) {
        if (subbed && sel.plan) {
          this.planInput.value = sel.plan.id;
          this.planInput.disabled = false;
        } else {
          this.planInput.value = '';
          this.planInput.disabled = true;
        }
      }

      if (this.atc && this.atcLabel) {
        if (!variant.available) {
          this.atc.setAttribute('disabled', '');
          this.atcLabel.textContent = 'Sold out';
        } else {
          this.atc.removeAttribute('disabled');
          var atcText = preorder ? 'Pre-order' : (subbed && sel.plan ? 'Add Subscription' : this.atcDefaultLabel);
          if (introOff) {
            this.atcLabel.innerHTML = atcText + ' · <s class="bundle-deal__atc-strike">'
              + formatMoney(sel.total, this.moneyFormat) + '</s> <span class="bundle-deal__atc-pay">'
              + formatMoney(payTotal, this.moneyFormat) + '</span>';
          } else {
            this.atcLabel.textContent = atcText + ' · ' + formatMoney(sel.total, this.moneyFormat);
          }
        }
      }

      // Keep afterpay messaging + sticky bar in sync with the selected variant.
      if (this.idInput) this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Toggle the reward badges above the ATC. Each badge animates itself in
    // (bubbly pop) / out (disintegrate) via the `is-on` class in CSS.
    //   supply — on whenever supply days are known: days x delivered units
    //   gift   — one-time 3 Pack only (the free 4th unit)
    //   ship   — one-time 2/3 Pack, or any subscription (always ships free)
    //   sub    — Subscribe + Save is active (shows the live % saved)
    //   credit — store credit earned on this pack (both modes)
    updatePerks(variant, sel, baseDays, credit) {
      if (!this.perks) return;
      var tier = this.state.tier;
      var subbed = this.subActive() && !!sel.plan;

      if (baseDays && this.supplyLabel) {
        this.supplyLabel.textContent = (baseDays * sel.supplyUnits) + '-day supply';
      }
      this.togglePerk('supply', !!baseDays);

      this.togglePerk('gift', sel.gift);
      if (this.giftWorth) this.giftWorth.textContent = '· worth ' + formatMoney(variant.price, this.moneyFormat);

      var shipBadge = this.perkEls.ship;
      var shipOn = subbed || (this.shipThreshold > 0 && sel.total >= this.shipThreshold);
      this.togglePerk('ship', shipOn);
      if (shipOn && this.shipLabel && shipBadge) {
        this.shipLabel.textContent = subbed
          ? (shipBadge.dataset.shipSub || 'Always ships free')
          : (tier === '3' ? (shipBadge.dataset.ship3 || 'Free Express Shipping') : (shipBadge.dataset.ship2 || 'Free Shipping'));
      }

      if (this.creditBadgeAmt) this.creditBadgeAmt.textContent = '$' + (credit || 0);
      this.togglePerk('credit', credit > 0);

      this.togglePerk('sub', subbed);
      if (subbed && this.subLabel) {
        this.subLabel.textContent = sel.pct > 0 ? 'Subscribed · Save ' + sel.pct + '%' : 'Subscribed';
      }

      // First-order hook badge — only once CLAIMED, one-time mode only
      // (pre-claim it would just duplicate the chip and confuse).
      this.togglePerk('intro', !subbed && this.introClaimed && !!this.introPerkLabel);
      if (this.introPerkLabel) {
        this.introPerkLabel.textContent = this.introPerkDefault + ' — applied';
      }

      // Benefits list: first line tracks the selected pack's actual %.
      var pctEl = this.querySelector('[data-benefit-save-pct]');
      if (pctEl) {
        pctEl.textContent = (subbed && sel.plan && sel.pct > 0)
          ? sel.pct + '%'
          : this.subPcts[3] + '%';
      }
    }

    togglePerk(key, on) {
      var el = this.perkEls[key];
      if (el) el.classList.toggle('is-on', !!on);
    }

    // Show/hide the pre-order delivery timeline and fill in the estimated
    // milestone dates. All milestones are framed customer-side:
    //   At our warehouse = the product's preorder_eta metafield (data-eta)
    //   Shipped to you    = warehouse + 2 days (order processing)
    //   At your door      = warehouse + 7 days
    // With no ETA set, the warehouse date falls back to a 21-day estimate.
    updateTimeline(show) {
      var el = this.timeline;
      if (!el) return;
      el.hidden = !show;
      if (!show) return;

      var SHIP_LEAD_DAYS = 2; // warehouse arrival -> dispatched to customer
      var DOOR_LEAD_DAYS = 7; // warehouse arrival -> customer's door

      var today = startOfDay(new Date());
      var warehouse = null;
      var etaStr = el.dataset.eta;
      if (etaStr) {
        var parts = etaStr.split('-');
        var parsed = startOfDay(new Date(+parts[0], +parts[1] - 1, +parts[2]));
        if (!isNaN(parsed) && daysBetween(today, parsed) >= 2) warehouse = parsed;
      }
      if (!warehouse) warehouse = addDays(today, 21);

      var dates = {
        1: warehouse,
        2: addDays(warehouse, SHIP_LEAD_DAYS),
        3: addDays(warehouse, DOOR_LEAD_DAYS),
      };

      Object.keys(dates).forEach((i) => {
        var span = el.querySelector('[data-step-date="' + i + '"]');
        if (span) span.textContent = '~' + formatShipDate(dates[i]);
      });

      // Only the first milestone is complete on a fresh pre-order; the next one
      // is highlighted as "current".
      var steps = el.querySelectorAll('.bundle-deal__step');
      steps.forEach((step, i) => {
        step.classList.toggle('is-done', i === 0);
        step.classList.toggle('is-next', i === 1);
      });
    }
  }

  if (!customElements.get('bundle-deal')) customElements.define('bundle-deal', BundleDeal);
})();
